import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  Layout,
  Banner,
  Stack,
  Button,
  ResourceList,
  ResourceItem,
  TextStyle,
  Badge,
  Spinner,
  Icon,
  Modal,
  TextContainer,
  Heading,
  Tooltip,
  Link,
  Filters,
  Pagination,
  Text,
  Select
} from '@shopify/polaris';
import { 
  ReceiptMajor, 
  CancelMajor, 
  CircleAlertMajor, 
  CircleTickMajor, 
  RefundMajor,
  ExternalMinor,
  SearchMajor
} from '@shopify/polaris-icons';
import { useAuth } from '../contexts/AuthContext';
import { cancelSubscription } from '../services/subscriptionService';
import { getUserPaymentHistory, testStripeServerConnectivity } from '../services/paymentService';

const PaymentHistory = () => {
  const { user } = useAuth();
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [serverConnectivity, setServerConnectivity] = useState({ tested: false, connected: false });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Search and filter state
  const [searchValue, setSearchValue] = useState('');
  const [appliedFilters, setAppliedFilters] = useState([]);

  // Test Stripe server connectivity
  const testServerConnectivity = async () => {
    try {
      const result = await testStripeServerConnectivity();
      console.log('Stripe server connectivity test result:', result);
      
      setServerConnectivity({
        tested: true,
        connected: result.success,
        details: result
      });
      
      return result.success;
    } catch (error) {
      console.error('Error testing Stripe server connectivity:', error);
      setServerConnectivity({
        tested: true,
        connected: false,
        error: error.message
      });
      return false;
    }
  };

  // Define fetchPaymentHistory outside useEffect so it can be used elsewhere
  const fetchPaymentHistory = async () => {
    setLoading(true);
    try {
      // First test connectivity to the Stripe server
      const isConnected = await testServerConnectivity();
      
      // Get payment history (will use Firestore fallback if server is not connected)
      const { payments, subscription } = await getUserPaymentHistory(user.uid);
      setPaymentHistory(payments || []);
      setFilteredPayments(payments || []);
      setSubscriptionInfo(subscription);
      
      // Only show error if we're not connected to Stripe server AND we don't have any payment data
      if (!isConnected && (!payments || payments.length === 0)) {
        setError('Unable to connect to payment server. Please try again later or contact support if the problem persists.');
      } else {
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching payment history:', err);
      setError('Failed to load payment history. Please try again later.');
      setPaymentHistory([]);
      setFilteredPayments([]);
      setSubscriptionInfo(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchPaymentHistory();
    }
  }, [user]);
  
  // Filter payments when search or filters change
  useEffect(() => {
    if (paymentHistory.length === 0) return;
    
    let filtered = [...paymentHistory];
    
    // Apply search
    if (searchValue) {
      const lowerSearch = searchValue.toLowerCase();
      filtered = filtered.filter(payment => {
        return (
          (payment.description && payment.description.toLowerCase().includes(lowerSearch)) ||
          (payment.invoiceNumber && payment.invoiceNumber.toLowerCase().includes(lowerSearch)) ||
          (payment.status && payment.status.toLowerCase().includes(lowerSearch)) ||
          (payment.type && payment.type.toLowerCase().includes(lowerSearch))
        );
      });
    }
    
    // Apply filters
    if (appliedFilters.length > 0) {
      appliedFilters.forEach(filter => {
        if (filter.key === 'status') {
          filtered = filtered.filter(payment => payment.status === filter.value);
        } else if (filter.key === 'type') {
          filtered = filtered.filter(payment => payment.type === filter.value);
        }
      });
    }
    
    setFilteredPayments(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchValue, appliedFilters, paymentHistory]);

  const handleCancelSubscription = async () => {
    try {
      setCancellingSubscription(true);
      
      if (!subscriptionInfo || !subscriptionInfo.stripeSubscriptionId) {
        throw new Error('No active subscription found');
      }
      
      const result = await cancelSubscription(user.uid, subscriptionInfo.stripeSubscriptionId);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to cancel subscription');
      }
      
      await fetchPaymentHistory(); // Refresh data
      setShowCancelModal(false);
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError('Failed to cancel subscription. Please try again.');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      // Handle different timestamp formats
      let date;
      
      // Handle Firestore timestamps
      if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (typeof timestamp === 'object' && timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'string') {
        // Try to parse the string date
        if (timestamp.includes('T')) {
          // ISO format
          date = new Date(timestamp);
        } else if (!isNaN(Date.parse(timestamp))) {
          // Other parsable string format
          date = new Date(timestamp);
        } else {
          // Try to handle numeric string
          date = new Date(parseInt(timestamp));
        }
      } else if (typeof timestamp === 'number') {
        // Handle milliseconds timestamp
        date = new Date(timestamp);
      } else {
        console.warn('Unrecognized date format:', timestamp);
        return 'Invalid date';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date object created from:', timestamp);
        return 'Invalid date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'Date error';
    }
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '$0.00';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      signDisplay: 'always'
    }).format(amount);
  };
  
  const getStatusBadge = (status) => {
    let badgeStatus = 'attention';
    let statusText = status;
    
    switch (status) {
      case 'paid':
        badgeStatus = 'success';
        statusText = 'Paid';
        break;
      case 'refunded':
        badgeStatus = 'warning';
        statusText = 'Refunded';
        break;
      case 'active':
        badgeStatus = 'success';
        statusText = 'Active';
        break;
      case 'open':
        badgeStatus = 'info';
        statusText = 'Open';
        break;
      case 'void':
        badgeStatus = 'critical';
        statusText = 'Void';
        break;
      default:
        badgeStatus = 'attention';
        statusText = status.charAt(0).toUpperCase() + status.slice(1);
    }
    
    return <Badge status={badgeStatus}>{statusText}</Badge>;
  };
  
  const getBillingPeriodText = (billingPeriod) => {
    if (!billingPeriod) return '';
    
    if (billingPeriod === 'month') return 'Monthly';
    if (billingPeriod === 'year') return 'Yearly';
    if (billingPeriod === '1-year') return 'Yearly';
    
    return billingPeriod;
  };

  // Get current payments for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPayments.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  
  // Handle filter removal and clear all
  const handleRemoveFilter = (key) => {
    setAppliedFilters(appliedFilters.filter(filter => filter.key !== key));
  };
  
  const handleClearAll = () => {
    setAppliedFilters([]);
    setSearchValue('');
  };
  
  // Handle filter selection
  const handleStatusFilterChange = (value) => {
    setAppliedFilters(current => {
      const newFilters = current.filter(filter => filter.key !== 'status');
      if (value) {
        newFilters.push({
          key: 'status',
          value: value,
          label: value.charAt(0).toUpperCase() + value.slice(1)
        });
      }
      return newFilters;
    });
  };
  
  const handleTypeFilterChange = (value) => {
    setAppliedFilters(current => {
      const newFilters = current.filter(filter => filter.key !== 'type');
      if (value) {
        newFilters.push({
          key: 'type',
          value: value,
          label: value.charAt(0).toUpperCase() + value.slice(1)
        });
      }
      return newFilters;
    });
  };
  
  // Filter configuration
  const filterConfig = [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <Select
          label="Status"
          labelHidden
          options={[
            {label: 'Paid', value: 'paid'},
            {label: 'Refunded', value: 'refunded'},
            {label: 'Active', value: 'active'},
            {label: 'Open', value: 'open'},
            {label: 'Void', value: 'void'}
          ]}
          onChange={handleStatusFilterChange}
          value={appliedFilters.find(filter => filter.key === 'status')?.value || ''}
        />
      ),
      shortcut: true
    },
    {
      key: 'type',
      label: 'Type',
      filter: (
        <Select
          label="Type"
          labelHidden
          options={[
            {label: 'Subscription', value: 'subscription'},
            {label: 'Credit', value: 'credit'},
            {label: 'Refund', value: 'refund'}
          ]}
          onChange={handleTypeFilterChange}
          value={appliedFilters.find(filter => filter.key === 'type')?.value || ''}
        />
      ),
      shortcut: true
    }
  ];

  if (loading) {
    return (
      <Page title="Subscription & Payment History">
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <Stack distribution="center">
                <Spinner size="large" />
              </Stack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Subscription & Payment History">
        <Layout>
          <Layout.Section>
            <Banner status="critical">
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page 
      title="Subscription & Payment History"
      subtitle="Manage your subscription and view payment history"
    >
      <Layout>
        {/* Server Connectivity Warning */}
        {serverConnectivity.tested && !serverConnectivity.connected && (
          <Layout.Section>
            <Banner status="warning" title="Limited Payment Information">
              <p>We're currently unable to connect to our payment server at stripe.geteditify.com. Your basic payment history is still available, but invoice links and detailed payment information may be missing. Our team has been notified of this issue.</p>
              <div style={{ marginTop: '10px' }}>
                <Button onClick={fetchPaymentHistory}>Retry Connection</Button>
              </div>
            </Banner>
          </Layout.Section>
        )}
        
        {/* Subscription Information */}
        <Layout.Section>
          <Card title="Current Subscription">
            <Card.Section>
              {subscriptionInfo ? (
                <Stack vertical spacing="loose">
                  <Stack distribution="equalSpacing" alignment="center">
                    <Stack vertical spacing="extraTight">
                      <TextStyle variation="strong">Plan</TextStyle>
                      <div>
                        {console.log('Payment History - Subscription Info:', subscriptionInfo)}
                        {(() => {
                          // Get the correct plan name based on the subscription type
                          let displayPlanName = subscriptionInfo.planName;
                          
                          // Override with the correct plan name if we have a subscription type
                          if (subscriptionInfo.type === 'plan_5') {
                            displayPlanName = 'Ultimate';
                            console.log('Overriding plan name to Ultimate for plan_5');
                          } else if (subscriptionInfo.type === 'plan_4') {
                            displayPlanName = 'Enterprise';
                          } else if (subscriptionInfo.type === 'plan_3') {
                            displayPlanName = 'Premium';
                          } else if (subscriptionInfo.type === 'plan_2') {
                            displayPlanName = 'Standard';
                          } else if (subscriptionInfo.type === 'plan_1') {
                            displayPlanName = 'Basic';
                          }
                          
                          return <Badge status="success">{displayPlanName}</Badge>;
                        })()}
                      </div>
                    </Stack>
                    
                    <Stack vertical spacing="extraTight">
                      <TextStyle variation="strong">Status</TextStyle>
                      <div>
                        {getStatusBadge(subscriptionInfo.status)}
                      </div>
                    </Stack>
                    
                    <Stack vertical spacing="extraTight">
                      <TextStyle variation="strong">Price</TextStyle>
                      <TextStyle>
                        {formatCurrency(subscriptionInfo.price)}
                        <TextStyle variation="subdued">
                          {subscriptionInfo.billingPeriod ? `/${getBillingPeriodText(subscriptionInfo.billingPeriod)}` : '/month'}
                        </TextStyle>
                      </TextStyle>
                    </Stack>
                    
                    <Stack vertical spacing="extraTight">
                      <TextStyle variation="strong">Next Billing Date</TextStyle>
                      <TextStyle>
                        {subscriptionInfo.cancelAtPeriodEnd 
                          ? 'Cancels on ' + formatDate(subscriptionInfo.nextBillingDate)
                          : formatDate(subscriptionInfo.nextBillingDate)
                        }
                      </TextStyle>
                    </Stack>
                  </Stack>
                  
                  {subscriptionInfo.status === 'active' && !subscriptionInfo.cancelAtPeriodEnd && (
                    <div style={{ textAlign: 'right' }}>
                      <Button 
                        destructive 
                        onClick={() => setShowCancelModal(true)}
                        icon={CancelMajor}
                      >
                        Cancel Subscription
                      </Button>
                    </div>
                  )}
                  
                  {subscriptionInfo.status === 'active' && subscriptionInfo.cancelAtPeriodEnd && (
                    <Banner status="warning">
                      <p>Your subscription is scheduled to be canceled at the end of the current billing period.</p>
                    </Banner>
                  )}
                </Stack>
              ) : (
                <Banner status="info">
                  <p>You don't have an active subscription.</p>
                </Banner>
              )}
            </Card.Section>
          </Card>
        </Layout.Section>

        {/* Payment History */}
        <Layout.Section>
          <Card title="Payment History">
            <Card.Section>
              <Filters
                queryValue={searchValue}
                filters={filterConfig}
                appliedFilters={appliedFilters.map(filter => ({
                  key: filter.key,
                  label: filter.label,
                  onRemove: () => handleRemoveFilter(filter.key)
                }))}
                onQueryChange={setSearchValue}
                onQueryClear={() => setSearchValue('')}
                onClearAll={handleClearAll}
              />
              
              {filteredPayments.length > 0 ? (
                <>
                  <ResourceList
                    resourceName={{ singular: 'payment', plural: 'payments' }}
                    items={currentItems}
                    renderItem={(item) => {
                      const { 
                        id, 
                        date, 
                        amount, 
                        description, 
                        status, 
                        invoiceUrl, 
                        invoicePdf, 
                        invoiceNumber,
                        receiptUrl,
                        type,
                        billingPeriod,
                        paymentMethod,
                        refundDate
                      } = item;
                      
                      // Choose icon based on payment type
                      let icon = ReceiptMajor;
                      if (type === 'refund') icon = RefundMajor;
                      
                      return (
                        <ResourceItem
                          id={id}
                          accessibilityLabel={`Payment for ${description}`}
                          media={<Icon source={icon} color={status === 'refunded' ? 'critical' : 'base'} />}
                          onClick={() => {}}
                        >
                          <Stack distribution="equalSpacing" alignment="center">
                            <Stack vertical spacing="extraTight">
                              <Stack spacing="tight" alignment="center">
                                <TextStyle variation="strong">{description}</TextStyle>
                                {invoiceNumber && (
                                  <Tooltip content="Invoice Number">
                                    <TextStyle variation="subdued">#{invoiceNumber}</TextStyle>
                                  </Tooltip>
                                )}
                                {billingPeriod && (
                                  <Badge>{getBillingPeriodText(billingPeriod)}</Badge>
                                )}
                              </Stack>
                              <TextStyle variation="subdued">{formatDate(date)}</TextStyle>
                              {paymentMethod && (
                                <TextStyle variation="subdued">{paymentMethod}</TextStyle>
                              )}
                              {status === 'refunded' && refundDate && (
                                <TextStyle variation="subdued">Refunded: {formatDate(refundDate)}</TextStyle>
                              )}
                            </Stack>
                            
                            <Stack vertical spacing="extraTight" alignment="center">
                              {getStatusBadge(status)}
                            </Stack>
                            
                            <TextStyle variation={status === 'refunded' ? 'negative' : 'positive'}>
                              {formatCurrency(amount)}
                            </TextStyle>
                            
                            <Stack spacing="tight">
                              {(invoiceUrl || invoicePdf || receiptUrl) && (
                                <Stack spacing="tight">
                                  {invoiceUrl && (
                                    <Tooltip content="View Invoice">
                                      <Button 
                                        plain 
                                        external 
                                        url={invoiceUrl}
                                        icon={ExternalMinor}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          window.open(invoiceUrl, '_blank');
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                  
                                  {invoicePdf && (
                                    <Tooltip content="Download Invoice PDF">
                                      <Button 
                                        plain 
                                        external 
                                        url={invoicePdf}
                                        icon={ExternalMinor}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          window.open(invoicePdf, '_blank');
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                  
                                  {receiptUrl && (
                                    <Tooltip content="View Receipt">
                                      <Button 
                                        plain 
                                        external 
                                        url={receiptUrl}
                                        icon={ExternalMinor}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          window.open(receiptUrl, '_blank');
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                  
                                  {!invoiceUrl && !invoicePdf && !receiptUrl && (
                                    <TextStyle variation="subdued">No invoice available</TextStyle>
                                  )}
                                </Stack>
                              )}
                            </Stack>
                          </Stack>
                        </ResourceItem>
                      );
                    }}
                  />
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                      <Pagination
                        hasPrevious={currentPage > 1}
                        onPrevious={() => setCurrentPage(currentPage - 1)}
                        hasNext={currentPage < totalPages}
                        onNext={() => setCurrentPage(currentPage + 1)}
                      />
                    </div>
                  )}
                  
                  {/* Page indicator */}
                  <div style={{ textAlign: 'center', marginTop: '10px' }}>
                    <Text variant="bodySm" as="p" color="subdued">
                      Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredPayments.length)} of {filteredPayments.length} payments
                    </Text>
                  </div>
                </>
              ) : (
                <div style={{ padding: '40px 0', textAlign: 'center' }}>
                  <Stack vertical spacing="tight" alignment="center">
                    <Icon source={ReceiptMajor} color="subdued" />
                    <TextStyle variation="subdued">No payment history found</TextStyle>
                    {searchValue || appliedFilters.length > 0 ? (
                      <Button onClick={handleClearAll}>Clear filters</Button>
                    ) : null}
                  </Stack>
                </div>
              )}
            </Card.Section>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Cancel Subscription Modal */}
      <Modal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Cancel Subscription"
        primaryAction={{
          content: 'Yes, Cancel Subscription',
          onAction: handleCancelSubscription,
          destructive: true,
          loading: cancellingSubscription
        }}
        secondaryActions={[
          {
            content: 'Keep My Subscription',
            onAction: () => setShowCancelModal(false)
          }
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>Are you sure you want to cancel your subscription?</p>
            <p>Your subscription will remain active until the end of the current billing period, but will not renew after that.</p>
          </TextContainer>
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default PaymentHistory;
